import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {Aangifte, BokkzIbDataService, IncomeTaxEntity,} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {filter, Observable, shareReplay, Subscription, take} from "rxjs";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiSearchControlModule} from "@snelstart/ui-search-control";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-opgaaf-wereldinkomen',
  templateUrl: './opgaaf-wereldinkomen.component.html',
  styleUrls: ['./opgaaf-wereldinkomen.component.scss'],
  imports: [
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiSearchControlModule,
  ]
})
export class OpgaafWereldinkomenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public isPartner = false;

  public formGroup= new FormGroup({
    winstUitOndernemingBuitenland: new FormControl<number | null>(null),
    loonTegenwoordigeDienstbetrekkingBuitenland: new FormControl<number | null>(null),
    pensioenEnAndereUitkeringenBuitenland: new FormControl<number | null>(null),
    afkoopsomLijfrenteBuitenland: new FormControl<number | null>(null),
    vrijgesteldLoonTegenwoordigeArbeidBuitenland: new FormControl<number | null>(null),
    vrijgesteldLoonVroegereArbeidBuitenland: new FormControl<number | null>(null),
    inkomstenOverigeWerkzaamhedenBuitenland: new FormControl<number | null>(null),
    inkomstenTerBeschikkingStellingBuitenland: new FormControl<number | null>(null),
    ontvangenAlimentatieBuitenland: new FormControl<number | null>(null),
    ontvangenPeriodiekeUitkeringenBuitenland: new FormControl<number | null>(null),
    reisAftrekOpenbaarVervoerBuitenland: new FormControl<number | null>(null),
    overigeInkomstenBuitenland: new FormControl<number | null>(null),
    uitgavenVoorInkomensvoorzieningBuitenland: new FormControl<number | null>(null),
    negatieveUitgavenVoorInkomensvoorzieningBuitenland: new FormControl<number | null>(null),
    saldoEigenWoningBuitenland: new FormControl<number | null>(null),
    aandeelSaldoEigenWoningBuitenland: new FormControl<number | null>(null),
    aftrekGeenOfGeringeEigenWoningSchuldBuitenland: new FormControl<number | null>(null),
    aandeelAftrekGeenOfGeringeEigenWoningSchuldBuitenland: new FormControl<number | null>(null),
    persoonsGebondenAftrekBuitenland: new FormControl<number | null>(null),
    negatievePersoonsGebondenAftrekBuitenland: new FormControl<number | null>(null),
    voordeelAanmerkelijkBelangBuitenland: new FormControl<number | null>(null),
    grondslagSparenEnBeleggenBuitenland: new FormControl<number | null>(null),
    voordeelSparenEnBeleggenBuitenland: new FormControl<number | null>(null),
    eldersBelastInkomenBox1: new FormControl<number | null>(null),
    eldersBelastInkomenBox2: new FormControl<number | null>(null),
    verhoudingInkomenNederlandWereld: new FormControl<number | null>(null),
    gezamenlijkeVerhoudingInkomenNederlandWereld: new FormControl<number | null>(null),
    geenIbWoonlandGeringInkomen: new FormControl<boolean | null>(false),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
    this.formGroup.patchValue(details.buitenlandseBelastingplicht?.wereldInkomen ?? {}, { emitEvent: false });
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const updateBelastbaarLoon = (aangifte: Aangifte) => ({
        ...aangifte,
        buitenlandseBelastingplicht: {
          ...aangifte.buitenlandseBelastingplicht,
          wereldInkomen: this.formGroup.getRawValue(),
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: updateBelastbaarLoon(currentEntity.aangifte?.partnerAangifte!),
      } : updateBelastbaarLoon(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
